import React, { useEffect } from 'react';
import { useJourneyGame, JOURNEY_STATES } from '../hooks/useJourneyGame';
import CityCard from './CityCard';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Trophy, Flame, Zap, Crown, Star, Map, ArrowRight, RotateCcw, Home } from 'lucide-react';

// Intro Screen
const JourneyIntro = ({ onStart, onBack, totalDecks }) => (
  <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 p-4 sm:p-8 flex items-center justify-center">
    <Card className="max-w-lg w-full bg-black/60 backdrop-blur-xl border-2 border-yellow-500/50 p-8 text-center">
      <div className="mb-6">
        <Map className="w-16 h-16 mx-auto text-yellow-400 mb-4" />
        <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 mb-2">
          THE JOURNEY
        </h1>
        <p className="text-gray-300 text-lg">The ultimate test of pop culture mastery</p>
      </div>
      
      <div className="space-y-4 mb-8 text-left">
        <div className="flex items-start gap-3 text-gray-200">
          <Zap className="w-5 h-5 text-yellow-400 mt-1 flex-shrink-0" />
          <span>Score <strong className="text-yellow-400">10 points</strong> to advance to the next deck</span>
        </div>
        <div className="flex items-start gap-3 text-gray-200">
          <Flame className="w-5 h-5 text-orange-400 mt-1 flex-shrink-0" />
          <span><strong className="text-orange-400">3 strikes</strong> and your journey ends</span>
        </div>
        <div className="flex items-start gap-3 text-gray-200">
          <Star className="w-5 h-5 text-cyan-400 mt-1 flex-shrink-0" />
          <span>Strikes <strong className="text-cyan-400">reset</strong> when you complete a deck</span>
        </div>
        <div className="flex items-start gap-3 text-gray-200">
          <Crown className="w-5 h-5 text-purple-400 mt-1 flex-shrink-0" />
          <span>Complete all <strong className="text-purple-400">{totalDecks} decks</strong> to achieve glory</span>
        </div>
      </div>
      
      <div className="space-y-3">
        <Button 
          onClick={onStart}
          className="w-full py-6 text-xl font-bold bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 hover:from-yellow-400 hover:via-orange-400 hover:to-red-400 text-black"
          data-testid="start-journey-btn"
        >
          BEGIN THE JOURNEY
        </Button>
        <Button 
          onClick={onBack}
          variant="ghost"
          className="w-full text-gray-400 hover:text-white"
        >
          <Home className="w-4 h-4 mr-2" />
          Back to Menu
        </Button>
      </div>
    </Card>
  </div>
);

// Deck Complete Celebration
const DeckCompleteCelebration = ({ celebration, decksCompleted, nextDeckInfo, onContinue, totalDecks }) => {
  // Escalating background effects based on progress
  const getBgClass = () => {
    if (decksCompleted >= 10) return 'from-yellow-600 via-orange-600 to-red-600';
    if (decksCompleted >= 7) return 'from-purple-600 via-pink-600 to-red-600';
    if (decksCompleted >= 5) return 'from-blue-600 via-purple-600 to-pink-600';
    if (decksCompleted >= 3) return 'from-green-600 via-teal-600 to-blue-600';
    return 'from-indigo-600 via-purple-600 to-pink-600';
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${getBgClass()} p-4 sm:p-8 flex items-center justify-center`}>
      <Card className="max-w-lg w-full bg-black/70 backdrop-blur-xl border-2 border-yellow-400 p-8 text-center animate-pulse-slow">
        {/* Celebration Emoji */}
        <div className="text-6xl mb-4 animate-bounce">
          {celebration.emoji}
        </div>
        
        {/* Title */}
        <h1 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-yellow-400 to-orange-500 mb-2">
          {celebration.title}
        </h1>
        <p className="text-xl text-gray-300 mb-6">{celebration.subtitle}</p>
        
        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-400 mb-2">
            <span>Decks Completed</span>
            <span className="text-yellow-400 font-bold">{decksCompleted} / {totalDecks}</span>
          </div>
          <Progress value={(decksCompleted / totalDecks) * 100} className="h-3 bg-gray-700" />
        </div>
        
        {/* Next Deck Preview */}
        {nextDeckInfo && (
          <div className="mb-6 p-4 bg-white/10 rounded-lg">
            <p className="text-sm text-gray-400 mb-1">Next Challenge</p>
            <p className="text-2xl font-bold text-white">
              {nextDeckInfo.icon} {nextDeckInfo.name}
            </p>
          </div>
        )}
        
        <Button 
          onClick={onContinue}
          className="w-full py-6 text-xl font-bold bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 text-black"
          data-testid="continue-journey-btn"
        >
          <ArrowRight className="w-6 h-6 mr-2" />
          Continue Journey
        </Button>
      </Card>
    </div>
  );
};

// Game Over Screen
const JourneyGameOver = ({ decksCompleted, totalDecks, onRestart, onBack, scoreSubmitted }) => (
  <div className="min-h-screen bg-gradient-to-br from-gray-900 via-red-900 to-gray-900 p-4 sm:p-8 flex items-center justify-center">
    <Card className="max-w-lg w-full bg-black/70 backdrop-blur-xl border-2 border-red-500/50 p-8 text-center">
      <div className="text-6xl mb-4">💀</div>
      <h1 className="text-4xl font-bold text-red-400 mb-2">Journey Over</h1>
      <p className="text-gray-400 mb-6">Your adventure has come to an end</p>
      
      <div className="bg-white/10 rounded-lg p-6 mb-6">
        <p className="text-sm text-gray-400 mb-1">Decks Passed</p>
        <p className="text-5xl font-black text-yellow-400">{decksCompleted}</p>
        <p className="text-sm text-gray-500 mt-1">out of {totalDecks}</p>
      </div>
      
      {scoreSubmitted && (
        <p className="text-green-400 text-sm mb-4">✓ Score saved to leaderboard</p>
      )}
      
      <div className="space-y-3">
        <Button 
          onClick={onRestart}
          className="w-full py-4 font-bold bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 text-black"
          data-testid="restart-journey-btn"
        >
          <RotateCcw className="w-5 h-5 mr-2" />
          Try Again
        </Button>
        <Button 
          onClick={onBack}
          variant="outline"
          className="w-full border-gray-600 text-gray-300 hover:bg-white/10"
        >
          <Home className="w-4 h-4 mr-2" />
          Back to Menu
        </Button>
      </div>
    </Card>
  </div>
);

// Journey Complete Screen
const JourneyComplete = ({ totalDecks, onRestart, onBack, scoreSubmitted }) => (
  <div className="min-h-screen bg-gradient-to-br from-yellow-600 via-orange-500 to-red-600 p-4 sm:p-8 flex items-center justify-center relative overflow-hidden">
    {/* Confetti-like particles */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(30)].map((_, i) => (
        <div
          key={i}
          className="absolute animate-float"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 3}s`,
            fontSize: `${1 + Math.random() * 2}rem`
          }}
        >
          {['⭐', '✨', '🏆', '👑', '🎉'][Math.floor(Math.random() * 5)]}
        </div>
      ))}
    </div>
    
    <Card className="max-w-lg w-full bg-black/80 backdrop-blur-xl border-4 border-yellow-400 p-8 text-center relative z-10">
      <div className="text-7xl mb-4 animate-bounce">🏆</div>
      <h1 className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-yellow-400 to-orange-400 mb-2">
        LEGENDARY!
      </h1>
      <p className="text-2xl text-yellow-200 mb-6">You completed The Journey!</p>
      
      <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-lg p-6 mb-6 border border-yellow-500/50">
        <p className="text-sm text-yellow-300 mb-1">All Decks Conquered</p>
        <p className="text-6xl font-black text-yellow-400">{totalDecks}</p>
        <p className="text-yellow-300 mt-2">🎖️ Master of Pop Culture 🎖️</p>
      </div>
      
      {scoreSubmitted && (
        <p className="text-green-400 text-sm mb-4">✓ Your legendary achievement has been recorded</p>
      )}
      
      <div className="space-y-3">
        <Button 
          onClick={onRestart}
          className="w-full py-4 font-bold bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 text-black"
        >
          <RotateCcw className="w-5 h-5 mr-2" />
          Start New Journey
        </Button>
        <Button 
          onClick={onBack}
          variant="outline"
          className="w-full border-yellow-500 text-yellow-400 hover:bg-yellow-500/10"
        >
          <Home className="w-4 h-4 mr-2" />
          Back to Menu
        </Button>
      </div>
    </Card>
  </div>
);

// Main Playing Screen
const JourneyPlaying = ({ game, onBack }) => {
  const { 
    deckInfo, currentCards, score, strikes, maxStrikes, pointsToAdvance, 
    decksCompleted, totalDecks, handleChoice, isRevealing, lastChoice,
    consecutiveCorrect, xRayVision, animatingCard, cardResult 
  } = game;

  // Safeguard - ensure we have valid data
  if (!deckInfo || !currentCards || currentCards.length < 2) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="text-4xl mb-4 animate-spin">🎲</div>
          <p className="text-xl">Loading deck...</p>
        </div>
      </div>
    );
  }

  const leftCard = currentCards[0];
  const rightCard = currentCards[1];

  // Additional safeguard for card validity
  if (!leftCard || !rightCard) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="text-4xl mb-4">⚠️</div>
          <p className="text-xl">Error loading cards</p>
          <Button onClick={onBack} className="mt-4">Back to Menu</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 p-2 sm:p-4">
      <div className="max-w-4xl mx-auto">
        {/* Compact Header */}
        <div className="flex justify-between items-center mb-2">
          <Button 
            onClick={onBack}
            variant="ghost"
            size="sm"
            className="text-gray-400 hover:text-white text-xs"
          >
            <Home className="w-3 h-3 mr-1" />
            Exit
          </Button>
          
          <div className="text-center">
            <span className="text-yellow-400 font-bold text-sm">🗺️ THE JOURNEY</span>
          </div>
          
          <div className="text-right">
            <span className="text-gray-400 text-xs">Decks: </span>
            <span className="text-yellow-400 font-bold text-sm">{decksCompleted}</span>
          </div>
        </div>

        {/* Current Deck & Score */}
        <div className="text-center mb-2">
          <h2 className="text-lg sm:text-xl font-bold text-white">
            {deckInfo.icon} {deckInfo.name}
          </h2>
          <div className="text-cyan-400 text-sm font-semibold">
            {score} / {pointsToAdvance}
          </div>
        </div>

        {/* Strikes - Matching Which'ing Hour style */}
        <div className="flex justify-center items-center gap-2 mb-3">
          {[...Array(maxStrikes)].map((_, i) => (
            <div key={i} className="relative">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                  i < strikes 
                    ? 'bg-gradient-to-br from-red-500 to-red-700 shadow-lg shadow-red-500/50' 
                    : 'bg-gray-800 border-2 border-gray-600'
                }`}
              >
                <span className={`text-xl ${i < strikes ? 'text-white' : 'text-gray-600'}`}>
                  ✕
                </span>
              </div>
            </div>
          ))}
          
          {/* Streak indicator */}
          {consecutiveCorrect > 0 && (
            <div className="ml-2 px-2 py-1 bg-orange-500/20 rounded-full border border-orange-500/50">
              <span className="text-orange-400 text-xs font-bold">🔥 {consecutiveCorrect}</span>
            </div>
          )}
        </div>

        {/* Game Area - Matching Which'ing Hour Layout */}
        <div className="mb-4 sm:mb-8">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 sm:p-8">
            {/* X-Ray Vision or Question */}
            {xRayVision ? (
              <div className="text-center mb-4 sm:mb-6">
                <div className="inline-block bg-cyan-600/80 px-4 py-2 rounded-lg animate-pulse">
                  <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center justify-center gap-2">
                    <span className="text-2xl sm:text-3xl">👁️</span>
                    X-Ray Vision
                    <span className="text-2xl sm:text-3xl">👁️</span>
                  </h2>
                  <p className="text-cyan-200 text-sm">Ranks revealed for this matchup!</p>
                </div>
              </div>
            ) : (
              <h2 className="text-xl sm:text-2xl font-bold text-white text-center mb-4 sm:mb-6">
                {deckInfo.promptText || 'Which is more popular?'}
              </h2>
            )}
            
            {/* Cards */}
            <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-8 items-center relative">
              {/* Match Point Alert - Positioned next to left card */}
              {score === 9 && (
                <div className="absolute left-0 sm:-left-10 top-1/2 -translate-y-1/2 z-20 hidden sm:block">
                  <div className="bg-gradient-to-b from-yellow-500 via-orange-500 to-red-500 px-2 py-4 rounded-lg animate-pulse border-2 border-yellow-300 shadow-lg shadow-yellow-500/50">
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-xl animate-bounce">🔥</span>
                      <span className="text-white font-black text-xs uppercase tracking-wider [writing-mode:vertical-lr] rotate-180">
                        Match Point!
                      </span>
                      <span className="text-xl animate-bounce">🔥</span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Mobile Match Point - Above cards on small screens */}
              {score === 9 && (
                <div className="sm:hidden absolute -top-16 left-1/2 -translate-x-1/2 z-20 w-full px-4">
                  <div className="bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 px-4 py-2 rounded-lg animate-pulse border-2 border-yellow-300 shadow-lg shadow-yellow-500/50">
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-xl animate-bounce">🔥</span>
                      <span className="text-white font-black text-xs uppercase tracking-wide">
                        Match Point!
                      </span>
                      <span className="text-xl animate-bounce">🔥</span>
                    </div>
                  </div>
                </div>
              )}
              
              {[leftCard, rightCard].map((card, index) => (
                <div 
                  key={card.id}
                  className="relative"
                >
                  <div
                    onClick={() => !isRevealing && handleChoice(card, index === 0 ? rightCard : leftCard)}
                    className={`transform transition-all duration-300 cursor-pointer hover:scale-105 
                      ${animatingCard === card.id 
                        ? cardResult === 'winner'
                          ? 'scale-110 ring-4 ring-green-500 animate-pulse'
                          : 'scale-90 ring-4 ring-red-500 opacity-75'
                        : ''
                      }`}
                  >
                    <CityCard 
                      card={card} 
                      showRank={xRayVision}
                      isWinner={animatingCard === card.id && cardResult === 'winner'}
                      isLoser={animatingCard === card.id && cardResult === 'loser'}
                      className="w-36 h-48 sm:w-48 sm:h-64 text-base sm:text-lg"
                      deckColors={deckInfo?.colors}
                      isAnimating={animatingCard === card.id}
                      animationType={animatingCard === card.id ? cardResult : null}
                    />
                  </div>
                  {/* Score Pop Animation */}
                  {animatingCard === card.id && cardResult === 'winner' && (
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 animate-bounce text-3xl sm:text-4xl font-bold text-green-400 z-50">
                      +1
                    </div>
                  )}
                  
                  {/* VS indicator between cards on mobile */}
                  {index === 0 && (
                    <div className="sm:hidden text-center my-2">
                      <span className="text-2xl font-bold text-yellow-400">VS</span>
                    </div>
                  )}
                </div>
              ))}
              
              {/* VS indicator for desktop (between cards) */}
              <div className="hidden sm:block absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                <span className="text-3xl font-bold text-yellow-400 drop-shadow-lg">VS</span>
              </div>
            </div>
            
            <p className="text-center text-gray-300 mt-4 sm:mt-6 text-sm sm:text-base">
              Click on the one you think ranks higher!
            </p>
          </div>
        </div>

        {/* Journey Progress Bar */}
        <div className="mt-4">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Journey Progress</span>
            <span>{decksCompleted} / {totalDecks} decks</span>
          </div>
          <Progress value={(decksCompleted / totalDecks) * 100} className="h-1.5 bg-gray-800" />
        </div>
      </div>
    </div>
  );
};

// Main Component
const JourneyGame = ({ onBack, user }) => {
  const game = useJourneyGame({ user });

  if (game.gameState === JOURNEY_STATES.INTRO) {
    return (
      <JourneyIntro 
        onStart={game.startJourney}
        onBack={onBack}
        totalDecks={game.totalDecks}
      />
    );
  }

  if (game.gameState === JOURNEY_STATES.DECK_COMPLETE) {
    return (
      <DeckCompleteCelebration
        celebration={game.celebration}
        decksCompleted={game.decksCompleted}
        nextDeckInfo={game.nextDeckInfo}
        onContinue={game.advanceToNextDeck}
        totalDecks={game.totalDecks}
      />
    );
  }

  if (game.gameState === JOURNEY_STATES.GAME_OVER) {
    return (
      <JourneyGameOver
        decksCompleted={game.decksCompleted}
        totalDecks={game.totalDecks}
        onRestart={game.restartJourney}
        onBack={onBack}
        scoreSubmitted={game.scoreSubmitted}
      />
    );
  }

  if (game.gameState === JOURNEY_STATES.JOURNEY_COMPLETE) {
    return (
      <JourneyComplete
        totalDecks={game.totalDecks}
        onRestart={game.restartJourney}
        onBack={onBack}
        scoreSubmitted={game.scoreSubmitted}
      />
    );
  }

  return (
    <JourneyPlaying
      game={game}
      onBack={onBack}
    />
  );
};

export default JourneyGame;
